// src/utils/phoneValidation.js
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

/**
 * Validates if a phone number is unique across all users
 * @param {string} phoneNumber - The phone number to validate
 * @param {string} currentUserId - The current user's ID (to exclude from check)
 * @returns {Promise<{isUnique: boolean, message: string}>}
 */
export const validatePhoneNumberUniqueness = async (phoneNumber, currentUserId = null) => {
  try {
    if (!phoneNumber || !phoneNumber.trim()) {
      return { isUnique: true, message: '' };
    }

    // Normalize phone number (remove spaces, dashes, etc.)
    const normalizedPhone = phoneNumber.trim().replace(/[\s\-\(\)]/g, '');
    
    if (normalizedPhone.length < 8) {
      return { isUnique: false, message: 'Phone number is too short' };
    }

    // Query users collection for existing phone numbers
    const usersRef = collection(db, 'users');
    const phoneQuery = query(usersRef, where('phoneNumber', '==', normalizedPhone));
    const querySnapshot = await getDocs(phoneQuery);

    // Check if any other user has this phone number
    const existingUsers = querySnapshot.docs.filter(doc => doc.id !== currentUserId);
    
    if (existingUsers.length > 0) {
      return { 
        isUnique: false, 
        message: 'This phone number is already registered by another user' 
      };
    }

    return { isUnique: true, message: '' };
  } catch (error) {
    console.error('Error validating phone number uniqueness:', error);
    return { 
      isUnique: false, 
      message: 'Error validating phone number. Please try again.' 
    };
  }
};

/**
 * Validates if a phone number format is correct for Madagascar
 * @param {string} phoneNumber - The phone number to validate
 * @returns {boolean}
 */
export const validatePhoneNumberFormat = (phoneNumber) => {
  if (!phoneNumber || !phoneNumber.trim()) {
    return false;
  }

  // Remove spaces, dashes, parentheses
  const normalizedPhone = phoneNumber.trim().replace(/[\s\-\(\)]/g, '');
  
  // Madagascar phone number patterns:
  // - Mobile: 034, 033, 032, 038 (Airtel, Orange, Telma)
  // - Format: 0341234567 (10 digits starting with 03)
  const madagascarPhoneRegex = /^03[2-8]\d{7}$/;
  
  return madagascarPhoneRegex.test(normalizedPhone);
};

/**
 * Normalizes a phone number for storage
 * @param {string} phoneNumber - The phone number to normalize
 * @returns {string}
 */
export const normalizePhoneNumber = (phoneNumber) => {
  if (!phoneNumber || !phoneNumber.trim()) {
    return '';
  }
  
  return phoneNumber.trim().replace(/[\s\-\(\)]/g, '');
};
